import type { IVerifyUser } from '@/features/auth/auth.types';
import type { Client } from '@/features/console/console.types';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Role that may switch tenants. Spelled out here rather than imported from the
 * console feature: that module reaches the booking axios instance, which reads
 * this store, and the import would close a cycle.
 */
const SUPER_ADMIN_ROLE = 'Admin';

interface SwitchNotice {
  /** Bumped on every switch so the banner remounts and restarts its timer. */
  id: number;
  companyName: string;
}

interface CompanyState {
  company?: Client.IClient;
  notice?: SwitchNotice;
  /**
   * Bumped once the previous tenant has been cleared away. Screens are keyed
   * on it, so they remount against a cache and a filter store that are already
   * empty — see `beginNewScope`.
   */
  scopeEpoch: number;
  /** `silent` skips the banner — used for the initial auto-selection. */
  setCompany: (company?: Client.IClient, silent?: boolean) => void;
  /**
   * Declares the previous tenant gone and the new one in force.
   *
   * Deliberately separate from `setCompany`. The company changes during
   * render, while the teardown that empties the cache and the filters runs
   * afterwards in an effect; remounting on the company itself would start the
   * new screen's queries just in time for that teardown to throw them away,
   * and leave the screen on no branch at all.
   */
  beginNewScope: () => void;
  dismissNotice: () => void;
  reset: () => void;
}

/**
 * Currently selected company (tenant). Driven by the company selector under
 * the sidebar logo; kept out of `filter.store` so resetting page filters never
 * clears the active company.
 *
 * Persisted so the choice survives an ordinary page reload: `protectedLoader`
 * re-verifies the session on every load and answers with the account's own
 * company, which for a super Admin is not the tenant they are looking at.
 * Cleared on login and on logout, so one operator's selection can never greet
 * the next one.
 */
export const useCompanyStore = create<CompanyState>()(
  persist(
    (set, get) => ({
      company: undefined,
      notice: undefined,
      scopeEpoch: 0,
      beginNewScope: () => set(state => ({ scopeEpoch: state.scopeEpoch + 1 })),
      setCompany: (company, silent = false) => {
        const previousId = get().company?.id;
        set({ company });

        // A silent set is the sidebar landing on the account's own company at
        // login, not somewhere the operator chose to go, so it raises no
        // banner.
        if (silent || !company?.name || company.id === previousId) return;

        set({ notice: { id: Date.now(), companyName: company.name } });
      },
      dismissNotice: () => set({ notice: undefined }),
      reset: () => set({ company: undefined, notice: undefined }),
    }),
    {
      name: 'company',
      // The company alone. The banner is deliberately left out: a switch no
      // longer reloads, so it is announced in the same document it was raised
      // in, and persisting it would bring a stale announcement back on an
      // ordinary refresh instead.
      partialize: state => ({ company: state.company }),
    }
  )
);

/**
 * Overlays the switched-to company onto a freshly verified user.
 *
 * `protectedLoader` re-verifies the session on every page load and both
 * layouts write its result straight into the auth store. Without this the
 * verify response would undo the switcher on each load: the roughly seventy
 * places that read `user.company` would snap back to whatever the API says,
 * which for a super Admin is not the tenant they are looking at.
 *
 * Doing it here — before the user object reaches the store — keeps it out of
 * effect ordering entirely. Only a super Admin has a selection to apply;
 * everyone else is returned untouched.
 */
export function withSelectedCompany(user: IVerifyUser): IVerifyUser {
  const selected = useCompanyStore.getState().company;

  if (!selected?.id || user.role?.name !== SUPER_ADMIN_ROLE) return user;

  return {
    ...user,
    company: {
      ...user.company,
      id: selected.id,
      name: selected.name,
      address: selected.address,
      status: selected.status,
    },
  };
}
